// Builds the JSON the local print service expects (see snippets/testPrintera.js)
// and sends it there. The service listens on localhost:3005 and does the printing.
//
// Prices on a receipt are the ones the customer pays, so the lines carry GROSS
// values while the tax recap carries the net base and the tax per rate.

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}
const r2 = (v) => Math.round(num(v) * 100) / 100

const PAYMENT_LABELS = {
  cash: 'Gotovina',
  card: 'Kartica',
  bank: 'Žiro račun'
}

const pad = (n) => (n < 10 ? '0' + n : String(n))

// "24.08.2026 14:32" - the format the print service prints as is
const printDate = (dateStr, timeStr) => {
  const d = dateStr ? new Date(dateStr) : new Date()
  const day = isNaN(d.getTime()) ? new Date() : d
  const date = pad(day.getDate()) + '.' + pad(day.getMonth() + 1) + '.' + day.getFullYear()
  const time = timeStr ? String(timeStr).substr(0, 5) : pad(day.getHours()) + ':' + pad(day.getMinutes())
  return date + ' ' + time
}

const joinAddress = (street, number) => [street, number].filter(x => x).join(' ').trim()
const joinCity = (postcode, city) => [postcode, city].filter(x => x).join(' ').trim()

/**
 * @param {Object} ctx
 *   cartItems, invoice, document, totals, cashTendered,
 *   company (store.companyActiv), user (store.user), branch (warehouse object)
 */
export const buildPrintPayload = (ctx) => {
  const cart = ctx.cartItems || []
  const invoice = ctx.invoice || {}
  const doc = ctx.document || {}
  const company = ctx.company || {}
  const user = ctx.user || {}
  const branch = ctx.branch || {}

  let subtotal = 0        // gross before discount
  let discountTotal = 0   // gross discount
  const ledger = {}       // per tax rate: net base and tax amount

  const items = cart.map(row => {
    const qty = num(row.qty)
    const basePrice = num(row.basePrice)       // net unit, before discount
    const discount = num(row.discount)         // net discount per unit
    const rate = num(row.taxRate)              // 0.25
    const factor = 1 + rate

    const grossUnit = basePrice * factor
    const grossBefore = grossUnit * qty
    const grossDiscount = discount * factor * qty
    const lineTotal = grossBefore - grossDiscount

    subtotal += grossBefore
    discountTotal += grossDiscount

    const netBase = (basePrice - discount) * qty
    const key = String(r2(rate * 100))
    if (!ledger[key]) { ledger[key] = {rate: r2(rate * 100), base: 0, amount: 0} }
    ledger[key].base += netBase
    ledger[key].amount += netBase * rate

    const item = {
      name: row.name || '',
      quantity: qty,
      unit: row.itemUnit || 'x',
      unitPrice: r2(grossUnit),
      total: r2(lineTotal),
      taxRate: r2(rate * 100)
    }
    if (grossDiscount > 0) { item.discount = r2(grossDiscount) }
    return item
  })

  const taxes = Object.keys(ledger)
    .map(k => ({rate: ledger[k].rate, base: r2(ledger[k].base), amount: r2(ledger[k].amount)}))
    .filter(t => t.base !== 0 || t.amount !== 0)
    .sort((a, b) => b.rate - a.rate)

  const total = ctx.totals && ctx.totals.finalTotal !== undefined
    ? r2(ctx.totals.finalTotal)
    : r2(subtotal - discountTotal)

  const tendered = num(ctx.cashTendered)
  const payments = [{
    method: PAYMENT_LABELS[invoice.paymentMethod] || 'Gotovina',
    amount: invoice.paymentMethod === 'cash' && tendered > 0 ? r2(tendered) : total
  }]

  const payload = {
    printer: ctx.printer || 'counter',
    jobId: ctx.jobId || (doc.QRcodeLink || String(Date.now()) + '-' + Math.random().toString(16).slice(2, 8)),
    openDrawer: invoice.paymentMethod === 'cash',
    copies: ctx.copies || 1,
    number: doc.documentIdGeneral || String(doc.documentId || ''),
    date: printDate(doc.documentDate || invoice.date, doc.documentTime || invoice.time),
    cashier: [user.First, user.Last].filter(x => x).join(' ') || (user.email || ''),
    seller: {
      name: company.CompanyName || '',
      address: joinAddress(company.CompanyStreet, company.CompanyBuildingNameNumber),
      city: joinCity(company.CompanyPostcode, company.CompanyCity),
      phone: company.CompanyPhones || '',
      taxId: company.CompanyVATID || ''
    },
    branch: {
      name: branch.WareHouseName || company.CompanyName || '',
      code: String(branch.WareHouseId || doc.WareHouseId || ''),
      address: joinAddress(company.CompanyStreet, company.CompanyBuildingNameNumber),
      city: joinCity(company.CompanyPostcode, company.CompanyCity),
      phone: company.CompanyPhones || ''
    },
    customer: {
      name: doc.partnerName || invoice.customerName || '',
      // a private buyer has no OIB, so the line is left out instead of printing "OIB: "
      taxId: doc.partnerVATID ? 'OIB: ' + doc.partnerVATID : ''
    },
    items: items,
    subtotal: r2(subtotal),
    discount: r2(discountTotal),
    taxes: taxes,
    total: total,
    payments: payments,
    footer: ctx.footer || 'Hvala na posjeti!'
  }

  if (invoice.paymentMethod === 'cash' && tendered > 0) {
    payload.change = r2(tendered - total)
  }

  // only send the fiscal block once the receipt really is fiscalised
  if (doc.documentJIR || doc.documentZKI) {
    payload.fiscal = {
      JIR: doc.documentJIR || '',
      ZKI: doc.documentZKI || '',
      qr: doc.QRcodeData || doc.QRcodeLink || ''
    }
  }

  return payload
}

// Servis prima ispis na /api/print/invoice. Ovdje je dosad stajalo samo
// "/print", sto taj servis vraca kao 404 - dakle nijedan racun s ove strane
// nikad nije zavrsio na papiru. Provjereno pozivom na zivom servisu.
const DEFAULT_PRINT_URL = 'http://localhost:3005/api/print/invoice'

// The print service always runs on the same machine as the till, but the port or the
// path can differ per workstation. Order:
//   1. localStorage 'printServiceUrl'  - set on the workstation, no rebuild needed
//   2. VUE_APP_PRINT_URL               - set for the whole build
//   3. localhost:3005/print            - default
export const printServiceUrl = () => {
  try {
    const saved = window.localStorage.getItem('printServiceUrl')
    if (saved && saved.trim()) { return saved.trim() }
  } catch (e) {
    // localStorage can be blocked - fall through to the build value
  }
  if (typeof process !== 'undefined' && process.env && process.env.VUE_APP_PRINT_URL) {
    return process.env.VUE_APP_PRINT_URL
  }
  return DEFAULT_PRINT_URL
}

export const PRINT_SERVICE_URL = DEFAULT_PRINT_URL

/**
 * Koji je pisac na OVOJ kasi. Servis ih ima vise ("counter", "posiflex",
 * "pdf"...), a koji je spojen zna samo radno mjesto - ne posluzitelj i ne
 * build. Postavlja se jednom po racunalu:
 *
 *   localStorage.setItem('printerName', 'posiflex')
 *
 * Prazno znaci: pusti ono sto je vec u podacima za ispis.
 */
export const printerName = () => {
  try {
    const saved = window.localStorage.getItem('printerName')
    return saved && saved.trim() ? saved.trim() : ''
  } catch (e) {
    return ''
  }
}

/**
 * Sends the payload to the local print service.
 * Resolves with {ok, status, body} - it never throws, so a printer that is not
 * running cannot stop the sale from being finished.
 */
export const sendToPrinter = async (payload, url) => {
  const target = url || printServiceUrl()
  try {
    const response = await fetch(target, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload)
    })
    let body = null
    const type = response.headers.get('content-type') || ''
    body = type.indexOf('application/json') !== -1 ? await response.json() : await response.text()
    return {ok: response.ok, status: response.status, body: body}
  } catch (err) {
    // service not started, wrong port, blocked by the browser ...
    return {ok: false, status: 0, body: String(err && err.message ? err.message : err)}
  }
}

export default {buildPrintPayload, sendToPrinter, printServiceUrl, printerName, PRINT_SERVICE_URL}
